import { useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  CalendarCheck,
  CornerDownRight,
  FileText,
  Info,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useOutlet } from "@/contexts/OutletContext";
import WorkflowActivity, { type CanvasWorkflowId } from "@/components/admin_dashboard/WorkflowActivity";
import AdminTabActionBar from "@/components/admin_dashboard/AdminTabActionBar";
import {
  InstagramCommentDmSettings,
  PostSessionFeedbackSettings,
} from "@/components/admin_dashboard/AcquisitionRetentionSettings";
import WorkflowSandbox from "@/components/admin_dashboard/WorkflowSandbox";
import {
  BirthdayMilestoneSettings,
  ComplaintRecoverySettings,
  DormantWinbackSettings,
  TrialToPaidSettings,
} from "@/components/admin_dashboard/RetentionExpansionSettings";
import FollowUpWavesEditor, {
  newWave,
  wavesFromServer,
  triggersFromServer,
  triggersToServer,
  defaultStopTriggers,
  type StopTriggers,
  wavesToServer,
  type FollowUpLiveStatus,
  type FollowUpWave,
} from "@/components/admin_dashboard/FollowUpWavesEditor";
import {
  InstagramIcon,
  LineIcon,
  MessengerIcon,
  TelegramIcon,
  TikTokIcon,
  WhatsAppIcon,
} from "@/components/admin_dashboard/BrandIcons";
import { useKnowledgeLibrarySources } from "@/components/admin_dashboard/knowledge-library/useKnowledgeLibrarySources";
import { commentDmTurnOnBlocker } from "@/lib/instagramCommentDm";

type IntegrationIcon = (props: { className?: string }) => JSX.Element;
interface WorkflowIntegration {
  label: string;
  Icon: IntegrationIcon;
}

const MESSAGING_INTEGRATIONS: readonly WorkflowIntegration[] = [
  { label: "WhatsApp", Icon: WhatsAppIcon },
  { label: "Instagram", Icon: InstagramIcon },
  { label: "Messenger", Icon: MessengerIcon },
  { label: "Telegram", Icon: TelegramIcon },
  { label: "LINE", Icon: LineIcon },
  { label: "TikTok", Icon: TikTokIcon },
];
const BOOKING_INTEGRATION: WorkflowIntegration = {
  label: "Dibs Bookings",
  Icon: ({ className }) => <CalendarCheck className={className} />,
};

const WORKFLOW_INTEGRATIONS: Record<string, readonly WorkflowIntegration[]> = {
  "new-lead-qualification": [...MESSAGING_INTEGRATIONS, BOOKING_INTEGRATION],
  // Comment webhooks and private replies are Instagram's.
  "instagram-comment-dm": [{ label: "Instagram", Icon: InstagramIcon }],
  "idle-lead-winback": MESSAGING_INTEGRATIONS,
  "complaint-recovery": MESSAGING_INTEGRATIONS,
};
const INTEGRATIONS_STACK_COUNT = 4;

function WorkflowIntegrations({ integrations }: { integrations: readonly WorkflowIntegration[] }) {
  if (integrations.length === 0) return null;
  const stacked = integrations.slice(0, INTEGRATIONS_STACK_COUNT);
  const hiddenCount = integrations.length - stacked.length;
  return (
    <div className="mt-3 flex items-center justify-between gap-3">
      <p className="text-[11px] font-medium text-foreground">Integrations used</p>
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`Integrations used: ${integrations.map((item) => item.label).join(", ")}`}
              className="flex items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {stacked.map(({ label, Icon }, index) => (
                <span
                  key={label}
                  className={`flex h-6 w-6 items-center justify-center rounded-full border border-border bg-card ${index > 0 ? "-ml-2" : ""}`}
                >
                  <Icon className="h-3 w-3" />
                </span>
              ))}
              {hiddenCount > 0 && (
                <span className="-ml-2 flex h-6 min-w-6 items-center justify-center rounded-full border border-border bg-muted px-1 text-[10px] font-medium text-muted-foreground">
                  +{hiddenCount}
                </span>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="end" className="p-2">
            <ul className="space-y-1.5">
              {integrations.map(({ label, Icon }) => (
                <li key={label} className="flex items-center gap-2 text-xs">
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </li>
              ))}
            </ul>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

const WORKFLOW_NODES = [
  {
    title: "Convert a new lead",
    workflowId: "new-lead-qualification",
    category: "Standard",
    // Backend automation_settings key; both are opt-in (off until turned on).
    automationKey: "lead_qualification",
    description: "Understand new enquiries and keep promising leads moving towards becoming customers.",
    logic: "When a new enquiry arrives, Dibs uses customer and business context to understand what they need, responds appropriately, and keeps the conversation moving towards a booking.",
  },
  {
    title: "Re-engage a new lead that ghosts",
    workflowId: "idle-lead-winback",
    category: "Standard",
    automationKey: "conversation_follow_up",
    description: "Follow up once when a new enquiry goes quiet.",
    logic: "If a new lead stops replying, Dibs waits for the quiet period, checks that a follow-up is appropriate, then sends one message to restart the conversation.",
  },
  {
    title: "Reply to comments with a DM",
    workflowId: "instagram-comment-dm",
    category: "Standard",
    automationKey: "instagram_comment_dm",
    description: "DM people who comment a keyword on your social posts.",
    logic: "When someone comments one of your keywords on a post or video, Dibs sends them a DM from your connected account and carries on the conversation from there.",
  },
  {
    title: "Ask for feedback after the first session",
    workflowId: "post-session-feedback",
    category: "Advanced",
    automationKey: "post_session_feedback",
    description: "Ask new customers how their first session went.",
    logic: "After a customer's first session, Dibs waits a little while and then asks how it went, so you can fix problems early and spot happy customers.",
  },
  {
    title: "Turn trial members into paying customers",
    workflowId: "trial-to-paid",
    category: "Standard",
    automationKey: "trial_to_paid",
    description: "Follow up after a trial to help people choose a plan.",
    logic: "After someone's trial, Dibs follows up at the right moment to help them choose a plan and become a paying customer.",
  },
  {
    title: "Bring back customers who stopped booking",
    workflowId: "dormant-winback",
    category: "Advanced",
    automationKey: "dormant_winback",
    description: "Message customers who haven't booked in a while.",
    logic: "When a past customer hasn't booked for a while, Dibs sends a friendly message inviting them back.",
  },
  {
    title: "Recover from customer complaints",
    workflowId: "complaint-recovery",
    category: "Standard",
    automationKey: "complaint_recovery",
    description: "Respond calmly when a customer is unhappy and bring in your team.",
    logic: "When a customer complains, Dibs acknowledges it, apologises where appropriate, and hands over to your team to put things right.",
  },
  {
    title: "Celebrate birthdays and milestones",
    workflowId: "birthday-milestone-message",
    category: "Advanced",
    automationKey: "birthday_milestone_message",
    description: "Send a personal message on a birthday or milestone.",
    logic: "On a customer's birthday, or when they reach a milestone such as their 10th class, Dibs sends them a warm personal message.",
  },
] as const;

type WorkflowId = (typeof WORKFLOW_NODES)[number]["workflowId"];
type WorkflowCategory = (typeof WORKFLOW_NODES)[number]["category"];

const WorkflowCategoryLabel = ({ category }: { category: WorkflowCategory }) => (
  <p className="text-[10px] font-semibold uppercase leading-4 tracking-wider text-muted-foreground">
    {category}
  </p>
);
type MessageContentMode = "ai" | "template";
type MessageSourceSelections = Record<MessageContentMode, string[]>;

// What "Convert a new lead" steers every AI reply towards. The live chat AI
// reads it (supabase/functions/_shared/knowledge/workflowSources.ts).
const CONVERSION_GOALS = [
  { value: "booking", label: "Get a booking" },
  { value: "trial", label: "Book a trial" },
  { value: "contact_details", label: "Get their contact details" },
  { value: "handover", label: "Hand over to the team" },
  { value: "custom", label: "Custom goal" },
] as const;
type ConversionGoal = (typeof CONVERSION_GOALS)[number]["value"];
const MAX_CUSTOM_GOAL = 300;

const parseConversionGoal = (value: unknown): ConversionGoal =>
  CONVERSION_GOALS.some((goal) => goal.value === value) ? (value as ConversionGoal) : "booking";

interface StoredMessageSettings {
  messageContentMode: MessageContentMode;
  selectedSourceIds: MessageSourceSelections;
  sourceNames: Record<string, string>;
  aiInstructions: string;
  conversionGoal: ConversionGoal;
  conversionGoalCustom: string;
}

const emptyMessageSettings = (): StoredMessageSettings => ({
  messageContentMode: "ai",
  selectedSourceIds: { ai: [], template: [] },
  sourceNames: {},
  aiInstructions: "",
  conversionGoal: "booking",
  conversionGoalCustom: "",
});

const MAX_AI_INSTRUCTIONS = 2000;

// The workflow these message settings belong to. Saved server-side in
// workflow_message_settings and read by the live chat AI for new leads
// (supabase/functions/_shared/knowledge/workflowSources.ts).
const MESSAGE_SETTINGS_WORKFLOW_ID = "new-lead-qualification";
// "Re-engage a new lead that ghosts" keeps its waves on the same table.
const FOLLOW_UP_WORKFLOW_ID = "idle-lead-winback";

const fromServerRow = (row: {
  content_mode?: string | null;
  ai_source_ids?: string[] | null;
  template_source_ids?: string[] | null;
  ai_instructions?: string | null;
  conversion_goal?: string | null;
  conversion_goal_custom?: string | null;
}): StoredMessageSettings => ({
  messageContentMode: row.content_mode === "template" ? "template" : "ai",
  selectedSourceIds: {
    ai: Array.isArray(row.ai_source_ids) ? row.ai_source_ids : [],
    template: Array.isArray(row.template_source_ids) ? row.template_source_ids : [],
  },
  sourceNames: {},
  aiInstructions: typeof row.ai_instructions === "string" ? row.ai_instructions : "",
  conversionGoal: parseConversionGoal(row.conversion_goal),
  conversionGoalCustom: typeof row.conversion_goal_custom === "string" ? row.conversion_goal_custom : "",
});

/** Settings saved in the browser before they were stored server-side. */
const readLegacyMessageSettings = (storageKey: string): StoredMessageSettings => {
  if (typeof window === "undefined") return emptyMessageSettings();

  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) ?? "null") as Partial<StoredMessageSettings> | null;
    if (!parsed || typeof parsed !== "object") return emptyMessageSettings();

    const selectedSourceIds = parsed.selectedSourceIds;
    const sourceNames = parsed.sourceNames && typeof parsed.sourceNames === "object"
      ? Object.fromEntries(
        Object.entries(parsed.sourceNames).filter((entry): entry is [string, string] => typeof entry[1] === "string"),
      )
      : {};
    return {
      messageContentMode: parsed.messageContentMode === "template" ? "template" : "ai",
      selectedSourceIds: {
        ai: Array.isArray(selectedSourceIds?.ai)
          ? selectedSourceIds.ai.filter((id): id is string => typeof id === "string")
          : [],
        template: Array.isArray(selectedSourceIds?.template)
          ? selectedSourceIds.template.filter((id): id is string => typeof id === "string")
          : [],
      },
      sourceNames,
      aiInstructions: "",
      conversionGoal: "booking",
      conversionGoalCustom: "",
    };
  } catch {
    return emptyMessageSettings();
  }
};

const hasSelections = (settings: StoredMessageSettings) =>
  settings.selectedSourceIds.ai.length > 0 || settings.selectedSourceIds.template.length > 0;

const WORKFLOW_SETTING_KEYS: Record<WorkflowId, string> = {
  "new-lead-qualification": "lead_qualification",
  "idle-lead-winback": "conversation_follow_up",
  "instagram-comment-dm": "instagram_comment_dm",
  "post-session-feedback": "post_session_feedback",
  "trial-to-paid": "trial_to_paid",
  "dormant-winback": "dormant_winback",
  "complaint-recovery": "complaint_recovery",
  "birthday-milestone-message": "birthday_milestone_message",
};

// Only these workflows can be opened and turned on for now; the rest are marked "Coming soon".
export const AVAILABLE_WORKFLOW_IDS: readonly WorkflowId[] = [
  "instagram-comment-dm",
  "new-lead-qualification",
  "idle-lead-winback",
  "complaint-recovery",
];

interface WorkflowCapabilitySettingsProps {
  businessId?: string;
}

const WORKFLOW_STAGES = [
  {
    id: "acquisition",
    label: "Acquisition",
    description: "How people find you.",
    workflowIds: ["instagram-comment-dm"],
  },
  {
    id: "conversion",
    label: "Conversion",
    description: "Turning enquiries into bookings.",
    workflowIds: ["new-lead-qualification", "idle-lead-winback", "trial-to-paid"],
  },
  {
    id: "retention",
    label: "Retention",
    description: "Keeping customers coming back.",
    workflowIds: ["post-session-feedback", "dormant-winback", "complaint-recovery"],
  },
  {
    id: "expansion",
    label: "Expansion",
    description: "Growing each customer's value.",
    workflowIds: ["birthday-milestone-message"],
  },
] as const satisfies readonly { id: string; label: string; description: string; workflowIds: readonly WorkflowId[] }[];

// Shown on cards that start from another workflow rather than from the funnel itself.
const WORKFLOW_NOTES: Partial<Record<WorkflowId, string>> = {
  "idle-lead-winback": "Starts when a lead from Convert a new lead goes quiet.",
};

type WorkflowView = "canvas" | "activity" | "sandbox";

const WorkflowViewTabs = ({ view, onChange }: { view: WorkflowView; onChange: (view: WorkflowView) => void }) => (
  // This page has no AdminLayout content padding for the bar's negative margins to cancel.
  <AdminTabActionBar className="admin-secondary-bar-tabs !mx-0 !mt-0 md:!mx-0">
    <div className="admin-underline-tabs" role="tablist" aria-label="Workflows view">
      {([["canvas", "Funnel"], ["activity", "Runs"], ["sandbox", "Sandbox"]] as const).map(([value, label]) => (
        <button
          key={value}
          type="button"
          role="tab"
          aria-selected={view === value}
          data-state={view === value ? "active" : "inactive"}
          className="admin-underline-tab outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          onClick={() => onChange(value)}
        >
          {label}
        </button>
      ))}
    </div>
  </AdminTabActionBar>
);

interface WorkflowCardProps {
  title: string;
  category: WorkflowCategory;
  description: string;
  note?: string;
  comingSoon: boolean;
  selected: boolean;
  enabled: boolean;
  onSelect: () => void;
  onEnabledChange: (enabled: boolean) => void;
  onOpenRuns: () => void;
}

const WorkflowCard = ({
  title,
  category,
  description,
  note,
  comingSoon,
  selected,
  enabled,
  onSelect,
  onEnabledChange,
  onOpenRuns,
}: WorkflowCardProps) => (
  <article
    data-workflow-card
    role="button"
    tabIndex={0}
    aria-label={`Edit ${title}`}
    aria-pressed={selected}
    className={`flex cursor-pointer flex-col rounded-lg border px-3 py-3 outline-none transition-[background-color,border-color,box-shadow] hover:shadow-md focus-visible:ring-2 focus-visible:ring-ring ${
      enabled ? "bg-card shadow-md" : "border-border bg-muted/70 shadow-sm"
    } ${
      selected
        ? "border-foreground/60 ring-1 ring-foreground/15"
        : enabled
          ? "border-foreground/30"
          : "border-border"
    }`}
    onClick={(event) => {
      if ((event.target as HTMLElement).closest("button")) return;
      onSelect();
    }}
    onKeyDown={(event) => {
      if ((event.target as HTMLElement).closest("button")) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onSelect();
      }
    }}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="text-sm font-semibold leading-5 text-foreground">{title}</h3>
      </div>
      <Switch
        checked={enabled}
        onCheckedChange={onEnabledChange}
        disabled={comingSoon}
        aria-label={`${enabled ? "Turn off" : "Turn on"} ${title}`}
        title={comingSoon ? "Coming soon" : `${enabled ? "Turn off" : "Turn on"} ${title}`}
        className="origin-right scale-90"
      />
    </div>
    <p className="mt-1 text-xs leading-4 text-muted-foreground">{description}</p>
    {note && (
      <p className="mt-1.5 flex items-start gap-1.5 text-xs leading-4 text-muted-foreground">
        <CornerDownRight className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
        {note}
      </p>
    )}
    <div className="mt-3 flex items-center justify-between gap-3">
      {comingSoon ? (
        <span className="rounded-full border border-border bg-background/70 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
          Coming soon
        </span>
      ) : (
        <button
          type="button"
          onClick={onOpenRuns}
          className="flex items-center rounded-sm text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Runs
          <ChevronRight className="ml-0.5 h-3.5 w-3.5" />
        </button>
      )}
      <span className="text-[11px] font-medium text-muted-foreground">{category}</span>
    </div>
  </article>
);

interface WorkflowCapabilitySettingsProps {
  /** Real on/off state from automation_settings (Automations owns it). */
  isAutomationEnabled?: (automationKey: string) => boolean;
  /** Turns a workflow on/off with the same plan and outlet checks as the list. */
  onAutomationToggle?: (automationKey: string, enabled: boolean) => void | Promise<void>;
  businessId?: string;
}

const WorkflowCapabilitySettings = ({
  isAutomationEnabled,
  onAutomationToggle,
  businessId: propBusinessId,
}: WorkflowCapabilitySettingsProps = {}) => {
  const { activeOutlet, isAllOutlets } = useOutlet();
  const { businessId: authBusinessId, user } = useAuth();
  const businessId = propBusinessId ?? activeOutlet?.business_id ?? authBusinessId;
  const queryClient = useQueryClient();
  const knowledgeSources = useKnowledgeLibrarySources(businessId);
  const legacyStorageKey = `dibs:workflow-message-settings:${businessId ?? "guest"}`;
  const messageSettingsQueryKey = ["workflow-message-settings", businessId, MESSAGE_SETTINGS_WORKFLOW_ID];
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<WorkflowId | null>(null);
  const [localWorkflowEnabled, setLocalWorkflowEnabled] = useState<Record<WorkflowId, boolean>>({
    "new-lead-qualification": true,
    "idle-lead-winback": false,
    "instagram-comment-dm": false,
    "post-session-feedback": false,
    "trial-to-paid": false,
    "dormant-winback": false,
    "complaint-recovery": false,
    "birthday-milestone-message": false,
  });

  // Fallback: load automation_settings from Supabase if isAutomationEnabled not supplied
  const { data: dbSettings } = useQuery({
    queryKey: ["automation-settings-capabilities", businessId],
    queryFn: async (): Promise<Record<string, boolean>> => {
      if (!businessId) return {};
      const { data, error } = await (supabase as any)
        .from("automation_settings")
        .select("automation_key, enabled")
        .eq("business_id", businessId)
        .in("automation_key", WORKFLOW_NODES.map((workflow) => workflow.automationKey));
      if (error) return {};
      const map: Record<string, boolean> = {};
      for (const row of (data ?? []) as Array<{ automation_key: string; enabled: boolean }>) {
        map[row.automation_key] = row.enabled;
      }
      return map;
    },
    enabled: !!businessId && !isAutomationEnabled,
  });

  const workflowEnabled = Object.fromEntries(
    WORKFLOW_NODES.map((workflow) => [
      workflow.workflowId,
      isAutomationEnabled
        ? isAutomationEnabled(workflow.automationKey)
        : (dbSettings?.[workflow.automationKey] ?? localWorkflowEnabled[workflow.workflowId] ?? false),
    ]),
  ) as Record<WorkflowId, boolean>;

  // null = nothing saved yet for this business.
  const { data: savedMessageSettings, isSuccess: messageSettingsLoaded } = useQuery({
    queryKey: messageSettingsQueryKey,
    enabled: !!businessId,
    queryFn: async (): Promise<StoredMessageSettings | null> => {
      const { data, error } = await (supabase as any)
        .from("workflow_message_settings")
        .select("content_mode, ai_source_ids, template_source_ids, ai_instructions, conversion_goal, conversion_goal_custom")
        .eq("business_id", businessId)
        .eq("workflow_id", MESSAGE_SETTINGS_WORKFLOW_ID)
        .maybeSingle();
      if (error) throw error;
      return data ? fromServerRow(data) : null;
    },
  });
  const followUpQueryKey = ["workflow-follow-up-waves", businessId, FOLLOW_UP_WORKFLOW_ID];
  const { data: savedFollowUp, isSuccess: wavesLoaded } = useQuery({
    queryKey: followUpQueryKey,
    enabled: !!businessId,
    queryFn: async (): Promise<{ waves: FollowUpWave[]; triggers: StopTriggers }> => {
      const { data, error } = await (supabase as any)
        .from("workflow_message_settings")
        .select("waves, stop_triggers")
        .eq("business_id", businessId)
        .eq("workflow_id", FOLLOW_UP_WORKFLOW_ID)
        .maybeSingle();
      if (error) throw error;
      return { waves: wavesFromServer(data?.waves), triggers: triggersFromServer(data?.stop_triggers) };
    },
  });
  // Until the owner saves anything, show one default wave (24 hours, AI-generated).
  const [fallbackWaves] = useState(() => [newWave(24)]);
  const followUpWaves = savedFollowUp && savedFollowUp.waves.length > 0 ? savedFollowUp.waves : fallbackWaves;
  const followUpTriggers = savedFollowUp?.triggers ?? defaultStopTriggers();

  // What's live for each wave, including its WhatsApp template's review.
  // The server compiles the saved waves into the running follow-up workflow
  // (supabase/functions/_shared/workflows/followUpWaves.ts).
  const followUpStatusKey = ["workflow-follow-up-status", businessId];
  const followUpRequest = async (action: "follow_up_status" | "sync_follow_up"): Promise<FollowUpLiveStatus> => {
    const { data, error } = await supabase.functions.invoke("workflow-definitions", {
      body: { action, business_id: businessId },
    });
    if (error || !data?.success) throw error ?? new Error(data?.error ?? "Follow-up status unavailable");
    return { waves: Array.isArray(data.waves) ? data.waves : [], issues: Array.isArray(data.issues) ? data.issues : [] };
  };
  const { data: followUpLive } = useQuery({
    queryKey: followUpStatusKey,
    enabled: !!businessId && selectedWorkflowId === FOLLOW_UP_WORKFLOW_ID,
    queryFn: () => followUpRequest("follow_up_status"),
    retry: false,
  });
  // Edits save at once; the live workflow follows once the owner pauses, so
  // a burst of edits publishes (and goes to WhatsApp) once.
  const followUpSyncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (followUpSyncTimer.current) clearTimeout(followUpSyncTimer.current);
  }, []);
  const scheduleFollowUpSync = () => {
    if (followUpSyncTimer.current) clearTimeout(followUpSyncTimer.current);
    followUpSyncTimer.current = setTimeout(() => {
      followUpSyncTimer.current = null;
      followUpRequest("sync_follow_up")
        .then((live) => queryClient.setQueryData(followUpStatusKey, live))
        // The hourly sync applies it anyway; the panel just can't show it yet.
        .catch(() => {});
    }, 1500);
  };

  const saveFollowUp = async (next: { waves: FollowUpWave[]; triggers: StopTriggers }) => {
    if (!businessId) return;
    queryClient.setQueryData(followUpQueryKey, next);
    const { error } = await (supabase as any)
      .from("workflow_message_settings")
      .upsert({
        business_id: businessId,
        workflow_id: FOLLOW_UP_WORKFLOW_ID,
        waves: wavesToServer(next.waves),
        stop_triggers: triggersToServer(next.triggers),
        updated_at: new Date().toISOString(),
        updated_by: user?.id ?? null,
      }, { onConflict: "business_id,workflow_id" });
    if (error) {
      toast.error("Couldn't save that setting. Please try again.");
      queryClient.invalidateQueries({ queryKey: followUpQueryKey });
      return;
    }
    scheduleFollowUpSync();
  };

  const messageSettings = savedMessageSettings ?? emptyMessageSettings();
  const { messageContentMode, selectedSourceIds } = messageSettings;
  const [instructionsDraft, setInstructionsDraft] = useState(messageSettings.aiInstructions);
  useEffect(() => {
    setInstructionsDraft(messageSettings.aiInstructions);
  }, [messageSettings.aiInstructions]);
  const [customGoalDraft, setCustomGoalDraft] = useState(messageSettings.conversionGoalCustom);
  useEffect(() => {
    setCustomGoalDraft(messageSettings.conversionGoalCustom);
  }, [messageSettings.conversionGoalCustom]);
  const [sourceQuery, setSourceQuery] = useState("");
  const [sourcePickerOpen, setSourcePickerOpen] = useState(false);
  const [view, setView] = useState<WorkflowView>("canvas");

  const [activityWorkflow, setActivityWorkflow] = useState<CanvasWorkflowId | null>(null);

  const saveMessageSettings = async (next: StoredMessageSettings) => {
    if (!businessId) return;
    queryClient.setQueryData(messageSettingsQueryKey, next);
    const { error } = await (supabase as any)
      .from("workflow_message_settings")
      .upsert({
        business_id: businessId,
        workflow_id: MESSAGE_SETTINGS_WORKFLOW_ID,
        content_mode: next.messageContentMode,
        ai_source_ids: next.selectedSourceIds.ai,
        template_source_ids: next.selectedSourceIds.template,
        ai_instructions: next.aiInstructions.slice(0, MAX_AI_INSTRUCTIONS),
        conversion_goal: next.conversionGoal,
        conversion_goal_custom: next.conversionGoalCustom.slice(0, MAX_CUSTOM_GOAL),
        updated_at: new Date().toISOString(),
        updated_by: user?.id ?? null,
      }, { onConflict: "business_id,workflow_id" });
    if (error) {
      toast.error("Couldn't save that setting. Please try again.");
      queryClient.invalidateQueries({ queryKey: messageSettingsQueryKey });
    }
  };

  // One-time move of choices saved in this browser before settings were
  // stored server-side, so nothing an owner already picked is lost.
  useEffect(() => {
    if (!businessId || !messageSettingsLoaded || savedMessageSettings) return;
    const legacy = readLegacyMessageSettings(legacyStorageKey);
    if (!hasSelections(legacy) && legacy.messageContentMode === "ai") return;
    void saveMessageSettings({ ...legacy, sourceNames: {} }).then(() => {
      try {
        window.localStorage.removeItem(legacyStorageKey);
      } catch {
        // Storage can be unavailable; the server copy is what counts.
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, messageSettingsLoaded, savedMessageSettings, legacyStorageKey]);

  const setMessageContentMode = (mode: MessageContentMode) => {
    void saveMessageSettings({ ...messageSettings, messageContentMode: mode });

    if (businessId && !isAllOutlets) {
      (supabase as any)
        .from("workflow_definitions")
        .select("id, definition")
        .eq("business_id", businessId)
        .eq("key", "lead_qualification")
        .maybeSingle()
        .then(({ data }: any) => {
          if (data?.id) {
            const currentDef = data.definition || {};
            (supabase as any)
              .from("workflow_definitions")
              .update({
                definition: { ...currentDef, content_mode: mode },
                updated_at: new Date().toISOString(),
              })
              .eq("id", data.id);
          }
        })
        .catch(() => {});
    }
  };

  const setSourceSelected = (
    mode: MessageContentMode,
    source: { id: string; name: string },
    selected: boolean,
  ) => {
    const current = messageSettings.selectedSourceIds[mode];
    void saveMessageSettings({
      ...messageSettings,
      selectedSourceIds: {
        ...messageSettings.selectedSourceIds,
        // A workflow uses one source per mode, so choosing replaces the previous one.
        [mode]: selected ? [source.id] : current.filter((id) => id !== source.id),
      },
    });
  };

  // Owner-written Knowledge only: notes, FAQs and Markdown files written in
  // the library. Websites and uploads are third-party text and never become
  // instructions (the server applies the same rule).
  const availableSources = (knowledgeSources.data ?? []).filter(
    (source) => source.type === "note" || source.type === "faq" ||
      (source.type === "file" && source.isMarkdownFile && !source.hasOriginalFile),
  );
  // Offers written in Knowledge → Offers, for workflows that can mention one.
  const availableOffers = (knowledgeSources.data ?? []).filter((source) => source.type === "offer");
  const normalizedSourceQuery = sourceQuery.trim().toLocaleLowerCase();
  const visibleSources = availableSources.filter((source) =>
    !normalizedSourceQuery
    || [source.name, source.content]
      .join(" ")
      .toLocaleLowerCase()
      .includes(normalizedSourceQuery),
  );
  const selectedSources = selectedSourceIds[messageContentMode]
    .map((sourceId) => availableSources.find((source) => source.id === sourceId) ?? {
      id: sourceId,
      name: knowledgeSources.isLoading ? "Loading…" : "Removed from Knowledge",
    });

  const setWorkflowStatus = async (workflowId: WorkflowId, enabled: boolean) => {
    const workflow = WORKFLOW_NODES.find((item) => item.workflowId === workflowId);
    if (!workflow) return;

    // Switched on before Instagram or the keywords are ready, it would look
    // live and never send anything.
    if (enabled && workflowId === "instagram-comment-dm" && businessId) {
      try {
        const blocker = await commentDmTurnOnBlocker(businessId);
        if (blocker) {
          toast.error(blocker.title, { description: blocker.description });
          return;
        }
      } catch (err) {
        console.warn("[WorkflowCapabilitySettings] comment-DM readiness check failed:", err);
      }
    }

    if (onAutomationToggle) {
      // Automations' toggle saves it, checks plan/outlet, and confirms with a toast.
      void onAutomationToggle(workflow.automationKey, enabled);
      return;
    }

    setLocalWorkflowEnabled((current) => ({ ...current, [workflowId]: enabled }));

    toast.success(`${workflow?.title ?? "Workflow"} turned ${enabled ? "on" : "off"}`, {
      description: enabled
        ? "It can now run when its conditions are met."
        : "It will stay visible, but it will not run.",
      action: {
        label: "Undo",
        onClick: () => {
          void setWorkflowStatus(workflowId, !enabled);
        },
      },
    });

    if (businessId && !isAllOutlets) {
      const key = workflow.automationKey;
      if (key) {
        (supabase as any)
          .from("automation_settings")
          .upsert(
            {
              business_id: businessId,
              automation_key: key,
              enabled,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "business_id,automation_key" },
          )
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ["automation-settings-capabilities", businessId] });
            queryClient.invalidateQueries({ queryKey: ["automation-settings", businessId] });
          })
          .catch((err: any) => {
            console.warn("[WorkflowCapabilitySettings] failed to save workflow status:", err);
          });
      }
    }
  };

  const selectedWorkflow = WORKFLOW_NODES.find(
    (workflow) => workflow.workflowId === selectedWorkflowId,
  );
  if (view === "activity") {
    return (
      <section aria-label="Workflow runs" className="flex h-full min-h-0 w-full flex-col bg-card">
        <WorkflowViewTabs view={view} onChange={setView} />
        <WorkflowActivity businessId={businessId} initialWorkflow={activityWorkflow} />
      </section>
    );
  }

  if (view === "sandbox") {
    return (
      <section aria-label="Workflow sandbox" className="flex h-full min-h-0 w-full flex-col">
        <WorkflowViewTabs view={view} onChange={setView} />
        <WorkflowSandbox
          businessId={businessId}
          workflows={WORKFLOW_NODES.map((workflow) => ({
            id: workflow.workflowId,
            title: workflow.title,
            available: AVAILABLE_WORKFLOW_IDS.includes(workflow.workflowId),
          }))}
        />
      </section>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
    <WorkflowViewTabs view={view} onChange={(next) => { setActivityWorkflow(null); setView(next); }} />
    <section
      aria-label="Workflows funnel"
      className="relative min-h-0 w-full flex-1 overflow-hidden bg-background"
      data-testid="workflow-capability-settings"
    >
      <div
        className="h-full overflow-y-auto p-4 pr-[340px] max-[720px]:pr-4"
        onClick={(event) => {
          if (!(event.target as HTMLElement).closest("[data-workflow-card]")) setSelectedWorkflowId(null);
        }}
      >
        <div
          role="status"
          className="flex items-start gap-2.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-blue-950 shadow-sm dark:border-blue-900/70 dark:bg-blue-950/60 dark:text-blue-100"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-300" />
          <p className="text-xs leading-5">
            Dibs personalises each customer&apos;s journey using purpose-built workflows. The
            Orchestration Agent decides which workflow to use and when.
          </p>
        </div>

        <ol className="mt-4 grid items-start gap-x-8 gap-y-6 md:grid-cols-2 xl:grid-cols-4">
          {WORKFLOW_STAGES.map((stage, index) => {
            const stageWorkflows = WORKFLOW_NODES.filter((workflow) =>
              (stage.workflowIds as readonly string[]).includes(workflow.workflowId));
            return (
              <li key={stage.id} aria-label={`${stage.label} stage`} className="min-w-0">
                <div className="relative flex items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2">
                  <span
                    aria-hidden="true"
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground"
                  >
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="text-sm font-semibold text-foreground">{stage.label}</h2>
                      {stageWorkflows.length === 0 && (
                        <span className="rounded-full border border-border bg-muted/60 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                          Coming soon
                        </span>
                      )}
                    </div>
                    <p className="truncate text-xs leading-4 text-muted-foreground" title={stage.description}>
                      {stage.description}
                    </p>
                  </div>
                  {index < WORKFLOW_STAGES.length - 1 && (
                    <ChevronRight
                      className="absolute -right-6 top-1/2 hidden h-4 w-4 -translate-y-1/2 text-muted-foreground xl:block"
                      aria-hidden="true"
                    />
                  )}
                </div>
                <div aria-label={`${stage.label} workflows`} className="mt-2 space-y-2">
                  {stageWorkflows.map((workflow) => {
                    const available = AVAILABLE_WORKFLOW_IDS.includes(workflow.workflowId);
                    return (
                      <WorkflowCard
                        key={workflow.workflowId}
                        title={workflow.title}
                        category={workflow.category}
                        description={workflow.description}
                        note={WORKFLOW_NOTES[workflow.workflowId]}
                        comingSoon={!available}
                        selected={selectedWorkflowId === workflow.workflowId}
                        enabled={available && workflowEnabled[workflow.workflowId]}
                        onSelect={() => setSelectedWorkflowId(workflow.workflowId)}
                        onEnabledChange={(enabled) => { void setWorkflowStatus(workflow.workflowId, enabled); }}
                        onOpenRuns={() => { setActivityWorkflow(workflow.workflowId as CanvasWorkflowId); setView("activity"); }}
                      />
                    );
                  })}
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      <aside
        aria-label="Workflow settings"
        className="absolute bottom-5 right-5 top-5 z-30 flex w-[calc(100%-2.5rem)] max-w-[300px] flex-col overflow-hidden rounded-lg border border-border bg-card shadow-lg"
      >
        {selectedWorkflowId !== null && (() => {
          const available = AVAILABLE_WORKFLOW_IDS.includes(selectedWorkflowId);
          const selectedEnabled = available && workflowEnabled[selectedWorkflowId];
          return (
            <section
              aria-labelledby="workflow-status-label"
              className={`flex items-center justify-between gap-4 border-b border-border px-5 py-3 transition-colors ${
                selectedEnabled ? "bg-emerald-50/80 dark:bg-emerald-950/30" : "bg-muted/20"
              }`}
            >
              <p id="workflow-status-label" className="text-xs font-medium text-foreground">
                {!available
                  ? "Coming soon. You can set it up, but it can't run yet."
                  : selectedEnabled
                    ? "On and ready to run"
                    : "Off and will not run"}
              </p>
              <Switch
                checked={selectedEnabled}
                disabled={!available}
                onCheckedChange={(enabled) => { void setWorkflowStatus(selectedWorkflowId, enabled); }}
                aria-label={`${selectedEnabled ? "Turn off" : "Turn on"} ${selectedWorkflow?.title ?? "workflow"}`}
                title={available ? undefined : "Coming soon"}
              />
            </section>
          );
        })()}

        <header className="border-b border-border px-5 py-4">
          {selectedWorkflow && (
            <>
              <WorkflowCategoryLabel category={selectedWorkflow.category} />
              <h2 className="text-base font-semibold leading-6 text-foreground">
                {selectedWorkflow.title}
              </h2>
              <p className="mt-1.5 text-xs leading-5 text-muted-foreground">
                {selectedWorkflow.logic}
              </p>
              <WorkflowIntegrations integrations={WORKFLOW_INTEGRATIONS[selectedWorkflow.workflowId] ?? []} />
            </>
          )}
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {selectedWorkflowId === null ? (
            <div className="rounded-md border border-dashed border-border bg-muted/30 px-4 py-5">
              <p className="text-sm font-medium text-foreground">Select a workflow</p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                Click a workflow card on the canvas to edit its settings.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <section aria-label="Workflow configuration">
                {selectedWorkflowId === "new-lead-qualification" ? (
                  <div className="space-y-6">
                  <div aria-labelledby="message-content-heading">
                    <div className="flex items-center gap-1.5">
                      <h3 id="message-content-heading" className="text-sm font-semibold text-foreground">
                        Message content
                      </h3>
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              aria-label="About message content"
                              className="rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <Info className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[220px] text-xs">
                            Choose how Dibs writes messages sent to new leads.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>

                    <RadioGroup
                      className="mt-4 gap-2"
                      value={messageContentMode}
                      onValueChange={(value) => setMessageContentMode(value as MessageContentMode)}
                      aria-label="Message content type"
                    >
                      <label
                        className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                          messageContentMode === "ai"
                            ? "border-foreground/35 bg-muted/60"
                            : "border-border hover:bg-muted/40"
                        }`}
                      >
                        <RadioGroupItem
                          value="ai"
                          aria-label="AI-generated"
                          className="mt-0.5 border-foreground text-foreground"
                        />
                        <span className="min-w-0">
                          <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                            <Sparkles className="h-3.5 w-3.5 text-primary" />
                            AI-generated
                          </span>
                          <span className="mt-1 block text-xs leading-4 text-muted-foreground">
                            Dibs writes each message using customer and knowledge context.
                          </span>
                        </span>
                      </label>

                      <label
                        className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                          messageContentMode === "template"
                            ? "border-foreground/35 bg-muted/60"
                            : "border-border hover:bg-muted/40"
                        }`}
                      >
                        <RadioGroupItem
                          value="template"
                          aria-label="Templated"
                          className="mt-0.5 border-foreground text-foreground"
                        />
                        <span className="min-w-0">
                          <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                            <FileText className="h-3.5 w-3.5" />
                            Templated
                          </span>
                          <span className="mt-1 block text-xs leading-4 text-muted-foreground">
                            Send the same pre-written content to every new lead.
                          </span>
                        </span>
                      </label>
                    </RadioGroup>

                    {messageContentMode === "ai" && (
                      <div className="mt-5">
                        <label htmlFor="new-lead-instructions" className="text-xs font-medium text-foreground">
                          Instructions for message generated
                        </label>
                        <Textarea
                          id="new-lead-instructions"
                          value={instructionsDraft}
                          maxLength={MAX_AI_INSTRUCTIONS}
                          disabled={!businessId}
                          placeholder="e.g. Always ask which class they're interested in, and offer a free trial."
                          className="mt-1.5 min-h-[120px] text-xs"
                          onChange={(event) => setInstructionsDraft(event.target.value)}
                          onBlur={() => {
                            if (instructionsDraft.trim() === messageSettings.aiInstructions.trim()) return;
                            void saveMessageSettings({ ...messageSettings, aiInstructions: instructionsDraft.trim() });
                          }}
                        />
                      </div>
                    )}

                    {(messageContentMode === "template" || selectedSourceIds.ai.length > 0) && (
                    <div className="mt-5">
                      <Popover open={sourcePickerOpen} onOpenChange={setSourcePickerOpen}>
                        <p id="message-source-heading" className="text-xs font-medium text-foreground">Source</p>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            aria-labelledby="message-source-heading"
                            disabled={!businessId}
                            className="mt-1.5 flex h-9 w-full items-center gap-2 rounded-md border border-input bg-card px-2.5 text-left text-xs transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                          >
                            <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                            <span className={`min-w-0 flex-1 truncate ${selectedSources.length ? "text-foreground" : "text-muted-foreground"}`}>
                              {selectedSources.length
                                ? `${selectedSources[0].name}${selectedSources.length > 1 ? ` +${selectedSources.length - 1}` : ""}`
                                : "Choose a source"}
                            </span>
                            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                          </button>
                        </PopoverTrigger>
                        <p className="mt-1.5 text-xs leading-5 text-muted-foreground">
                          Knowledge used for {messageContentMode === "ai" ? "AI-generated" : "templated"} replies.
                        </p>

                        <PopoverContent
                          align="start"
                          className="w-[var(--radix-popover-trigger-width)] p-2"
                          onPointerDown={(event) => event.stopPropagation()}
                          onWheel={(event) => event.stopPropagation()}
                        >
                          <p className="px-2 pb-1 pt-1 text-xs font-semibold text-foreground">
                            Knowledge database
                          </p>
                          <div className="relative my-1.5">
                            <Search
                              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                              aria-hidden="true"
                            />
                            <Input
                              value={sourceQuery}
                              onChange={(event) => setSourceQuery(event.target.value)}
                              placeholder="Search knowledge..."
                              className="h-8 pl-8 text-xs focus-visible:ring-0 focus-visible:ring-offset-0"
                              aria-label="Search knowledge"
                            />
                          </div>
                          <div className="max-h-64 overflow-y-auto">
                            {knowledgeSources.isLoading ? (
                              <p className="px-2 py-3 text-xs text-muted-foreground">Loading sources…</p>
                            ) : visibleSources.length === 0 ? (
                              <p className="px-2 py-3 text-xs leading-5 text-muted-foreground">
                                {availableSources.length === 0
                                  ? "No notes, FAQs or Markdown files are available yet."
                                  : "Nothing matches your search."}
                              </p>
                            ) : (
                              <div role="radiogroup" aria-label="Source">
                                {selectedSources.length > 0 && (
                                  <div
                                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-xs text-muted-foreground transition-colors hover:bg-muted"
                                    role="radio"
                                    tabIndex={0}
                                    aria-checked={false}
                                    onClick={() => {
                                      saveMessageSettings({
                                        ...messageSettings,
                                        selectedSourceIds: { ...messageSettings.selectedSourceIds, [messageContentMode]: [] },
                                      });
                                      setSourcePickerOpen(false);
                                    }}
                                    onKeyDown={(event) => {
                                      if (event.key !== "Enter" && event.key !== " ") return;
                                      event.preventDefault();
                                      event.currentTarget.click();
                                    }}
                                  >
                                    <X className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                    No source
                                  </div>
                                )}
                                {visibleSources.map((source) => {
                                  const selected = selectedSourceIds[messageContentMode].includes(source.id);
                                  const choose = () => {
                                    setSourceSelected(messageContentMode, source, true);
                                    setSourcePickerOpen(false);
                                  };
                                  return (
                                    <div
                                      key={source.id}
                                      className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 transition-colors hover:bg-muted ${selected ? "bg-muted/60" : ""}`}
                                      role="radio"
                                      tabIndex={0}
                                      aria-checked={selected}
                                      onClick={choose}
                                      onKeyDown={(event) => {
                                        if (event.key !== "Enter" && event.key !== " ") return;
                                        event.preventDefault();
                                        choose();
                                      }}
                                    >
                                      <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                                      <span className="min-w-0 flex-1">
                                        <span className="block truncate text-xs font-medium text-foreground">{source.name}</span>
                                        <span className="block text-[11px] text-muted-foreground">
                                          {source.type === "faq" ? "FAQ" : source.type === "file" ? "File" : "Note"} · {source.status}
                                        </span>
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                    )}
                  </div>

                  <div aria-labelledby="conversion-goal-heading">
                    <div className="flex items-center gap-1.5">
                      <h3 id="conversion-goal-heading" className="text-sm font-semibold text-foreground">
                        Conversion goal
                      </h3>
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              aria-label="About conversion goal"
                              className="rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <Info className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[220px] text-xs">
                            Choose what Dibs steers new leads towards in every reply.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Select
                      value={messageSettings.conversionGoal}
                      disabled={!businessId}
                      onValueChange={(value) => {
                        const conversionGoal = parseConversionGoal(value);
                        if (conversionGoal === messageSettings.conversionGoal) return;
                        void saveMessageSettings({ ...messageSettings, conversionGoal });
                      }}
                    >
                      <SelectTrigger aria-labelledby="conversion-goal-heading" className="mt-3 h-9 w-full text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONVERSION_GOALS.map((goal) => (
                          <SelectItem key={goal.value} value={goal.value} className="text-xs">{goal.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {messageSettings.conversionGoal === "custom" && (
                      <div className="mt-3">
                        <label htmlFor="conversion-goal-custom" className="text-xs font-medium text-foreground">
                          Your goal
                        </label>
                        <Textarea
                          id="conversion-goal-custom"
                          value={customGoalDraft}
                          maxLength={MAX_CUSTOM_GOAL}
                          disabled={!businessId}
                          placeholder="e.g. Get them to come to our Saturday open day."
                          className="mt-1.5 min-h-[72px] text-xs"
                          onChange={(event) => setCustomGoalDraft(event.target.value)}
                          onBlur={() => {
                            if (customGoalDraft.trim() === messageSettings.conversionGoalCustom.trim()) return;
                            void saveMessageSettings({ ...messageSettings, conversionGoalCustom: customGoalDraft.trim() });
                          }}
                        />
                        <p className="mt-1.5 text-xs leading-5 text-muted-foreground">
                          Until you write one, Dibs aims for a booking.
                        </p>
                      </div>
                    )}
                  </div>
                  </div>
                ) : selectedWorkflowId === "idle-lead-winback" ? (
                  <FollowUpWavesEditor
                    waves={followUpWaves}
                    sources={availableSources}
                    sourcesLoading={knowledgeSources.isLoading}
                    disabled={!businessId || !wavesLoaded}
                    triggers={followUpTriggers}
                    onCommit={(waves) => { void saveFollowUp({ waves, triggers: followUpTriggers }); }}
                    onTriggersCommit={(triggers) => { void saveFollowUp({ waves: followUpWaves, triggers }); }}
                    liveStatus={followUpLive}
                  />
                ) : selectedWorkflowId === "instagram-comment-dm" ? (
                  <InstagramCommentDmSettings key={businessId ?? "guest"} businessId={businessId} />
                ) : selectedWorkflowId === "post-session-feedback" ? (
                  <PostSessionFeedbackSettings
                    key={businessId ?? "guest"}
                    businessId={businessId}
                    sources={availableSources}
                    sourcesLoading={knowledgeSources.isLoading}
                  />
                ) : selectedWorkflowId === "trial-to-paid" ? (
                  <TrialToPaidSettings
                    key={businessId ?? "guest"}
                    businessId={businessId}
                    sources={availableSources}
                    sourcesLoading={knowledgeSources.isLoading}
                  />
                ) : selectedWorkflowId === "dormant-winback" ? (
                  <DormantWinbackSettings
                    key={businessId ?? "guest"}
                    businessId={businessId}
                    sources={availableSources}
                    sourcesLoading={knowledgeSources.isLoading}
                  />
                ) : selectedWorkflowId === "complaint-recovery" ? (
                  <ComplaintRecoverySettings
                    key={businessId ?? "guest"}
                    businessId={businessId}
                    offers={availableOffers}
                    sources={availableSources}
                    sourcesLoading={knowledgeSources.isLoading}
                  />
                ) : selectedWorkflowId === "birthday-milestone-message" ? (
                  <BirthdayMilestoneSettings
                    key={businessId ?? "guest"}
                    businessId={businessId}
                    offers={availableOffers}
                    sources={availableSources}
                    sourcesLoading={knowledgeSources.isLoading}
                  />
                ) : (
                  <div className="rounded-md border border-dashed border-border bg-muted/30 px-4 py-5">
                    <p className="text-sm font-medium text-foreground">No editable settings yet</p>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">
                      Settings for this workflow will appear here when available.
                    </p>
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </aside>

    </section>
    </div>
  );
};

export default WorkflowCapabilitySettings;
